import { useI18n } from "@/i18n/I18nProvider";
import { LogoMark } from "@/components/Logo";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d6f58c33-0a17-4479-ae12-e5dc7e28c8d7/images/43069519fc580e56639266bdefde4fb7e943904e4454d7e90621affb426addaa.png";

export default function Hero() {
  const { t } = useI18n();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-end overflow-hidden kh-noise"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 kh-slow-zoom bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        {/* Layered gradients: black + burgundy undertone */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/45 to-black" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#4A0F1C]/30 via-transparent to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      </div>

      {/* Royal blue diagonal accent bar (right side, like a film letterbox) */}
      <div className="hidden lg:block absolute top-0 right-0 h-full w-[28%] z-[1] kh-fade-in"
        style={{
          background: "linear-gradient(135deg, transparent 0%, transparent 40%, rgba(123, 30, 46, 0.22) 50%, rgba(123, 30, 46, 0.08) 100%)",
          clipPath: "polygon(40% 0, 100% 0, 100% 100%, 0 100%)"
        }}
      />

      {/* Cinematic top strip */}
      <div className="absolute top-20 left-0 right-0 z-10 flex items-center justify-between px-6 sm:px-8 md:px-12">
        <div className="flex items-center gap-3 kh-fade-in">
          <span className="h-px w-12 bg-[#D4AF37]" />
          <span className="kh-chapter">{t.hero.chapter}</span>
        </div>
        <div className="hidden md:flex items-center gap-3 kh-fade-in">
          <span className="font-body text-[10px] tracking-[0.35em] uppercase text-white/60">
            EST · MMXXVI
          </span>
          <span className="h-px w-12 bg-[#D4AF37]" />
        </div>
      </div>

      {/* Side rail with crown */}
      <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-6">
        <LogoMark size={32} color="#D4AF37" accent="#A93043" />
        <div className="w-px h-32 bg-gradient-to-b from-[#D4AF37] via-[#A93043] to-transparent" />
        <span className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] [writing-mode:vertical-rl] font-body">
          King Kirchner
        </span>
      </div>

      {/* Vertical side text - right */}
      <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-6">
        <span className="text-[10px] tracking-[0.4em] uppercase text-white/50 [writing-mode:vertical-rl] font-body">
          Vol. 01 — KirchHero
        </span>
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-[#A93043] to-[#D4AF37]" />
        <div className="w-2 h-2 bg-[#D4AF37] rotate-45" />
      </div>

      {/* Content — anchored bottom-left like a film poster credit */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12 w-full pb-24 lg:pb-32">
        <div className="max-w-3xl">
          <div className="kh-fade-up flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[#D4AF37]" />
            <span
              data-testid="hero-eyebrow"
              className="kh-eyebrow"
            >
              {t.hero.eyebrow}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="kh-fade-up-d1 font-display text-white leading-[0.85] tracking-tight uppercase"
            style={{ fontSize: "clamp(3.5rem, 11vw, 9rem)" }}
          >
            <span className="block">{t.hero.titleLine1}</span>
            <span className="block">
              <span className="text-[#D4AF37]">{t.hero.titleLine2.replace(".", "")}</span>
              <span className="text-[#A93043] font-serif-luxury italic ml-1">.</span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="kh-fade-up-d2 mt-8 max-w-xl font-body text-base sm:text-lg text-white/75 leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="kh-fade-up-d3 mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#shop"
              data-testid="hero-cta-shop"
              className="kh-btn-gold kh-pulse-gold"
            >
              {t.hero.ctaShop}
              <ArrowDown className="w-4 h-4" />
            </a>
            <a
              href="#story"
              data-testid="hero-cta-story"
              className="kh-btn-outline"
            >
              {t.hero.ctaStory}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom cinematic strip — like film credits */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-3 flex items-center justify-between text-[10px] sm:text-xs tracking-[0.3em] uppercase font-body">
          <span className="text-white/50">Vol. 01 — The Crown Edition</span>
          <span className="hidden md:flex items-center gap-2 text-white/70">
            <span className="text-[#D4AF37]">●</span> Now showing
          </span>
          <span className="text-[#D4AF37]">↓ Scroll</span>
        </div>
      </div>
    </section>
  );
}
